import React from 'react';
import { motion } from 'framer-motion';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePageContent } from '../hooks/usePageContent';

// -------- Rich text renderer --------
// Supports: paragraph splits on blank line, single \n = line break inside a paragraph,
// **bold** inline emphasis, and auto-links for URLs / emails.
const renderInline = (text) => {
  // Split on bold segments **text**
  const boldParts = text.split(/(\*\*[^*]+\*\*)/g);
  return boldParts.map((chunk, i) => {
    if (chunk.startsWith('**') && chunk.endsWith('**')) {
      return (
        <strong key={i} className="font-heading text-charcoal-800">
          {chunk.slice(2, -2)}
        </strong>
      );
    }
    // Auto-link emails
    const emailParts = chunk.split(/([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g);
    return emailParts.map((seg, j) => {
      if (/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(seg)) {
        return (
          <a
            key={`${i}-${j}`}
            href={`mailto:${seg}`}
            className="text-saffron-700 hover:text-saffron-600 underline"
          >
            {seg}
          </a>
        );
      }
      return <React.Fragment key={`${i}-${j}`}>{seg}</React.Fragment>;
    });
  });
};

const RichText = ({ text, className = '' }) => {
  if (!text) return null;
  const paragraphs = text.split(/\n\s*\n/);
  return (
    <>
      {paragraphs.map((para, i) => (
        <p key={i} className={className}>
          {para.split('\n').map((line, j) => (
            <React.Fragment key={j}>
              {j > 0 && <br />}
              {renderInline(line)}
            </React.Fragment>
          ))}
        </p>
      ))}
    </>
  );
};

// -------- Shared layout wrapper --------
const LegalPage = ({ pageId, defaults, testId }) => {
  usePageTitle(defaults.title);
  const { t } = usePageContent(pageId);

  return (
    <div className="min-h-screen pt-20 bg-cream" data-testid={testId}>
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="text-saffron-600 uppercase text-xs md:text-sm tracking-[0.25em] font-body font-semibold mb-4">
              {t('overline', defaults.overline || 'Legal')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-charcoal-800 tracking-tighter leading-none mb-4">
              {t('title', defaults.title)}
            </h1>
            <p className="text-sm text-charcoal-500 font-body italic">
              Last updated: {t('last_updated', defaults.last_updated)}
            </p>
          </motion.div>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(44,30,22,0.06)] border border-charcoal-100 p-8 md:p-12 space-y-8"
          >
            {/* Intro (optional) */}
            {(t('intro', defaults.intro) || '').trim() && (
              <div className="text-base md:text-lg text-charcoal-700 leading-relaxed font-body border-l-4 border-saffron-500 pl-5 py-1 bg-saffron-50/40 rounded-r-md space-y-3">
                <RichText text={t('intro', defaults.intro)} />
              </div>
            )}

            {/* Numbered sections */}
            {defaults.sections.map((sec, idx) => {
              const num = idx + 1;
              const heading = t(`section_${num}_heading`, sec.heading);
              const body = t(`section_${num}_body`, sec.body);
              if (!(heading || '').trim() && !(body || '').trim()) return null;
              return (
                <section key={num} data-testid={`${testId}-section-${num}`}>
                  <h2 className="text-xl md:text-2xl font-heading text-charcoal-800 mb-4 leading-snug">
                    <span className="text-saffron-600 mr-2">{num}.</span>
                    {heading}
                  </h2>
                  <div className="space-y-3 text-charcoal-600 text-base md:text-lg leading-relaxed font-body">
                    <RichText text={body} />
                  </div>
                </section>
              );
            })}
          </motion.article>
        </div>
      </section>
    </div>
  );
};

// ==================== 1. Terms & Conditions ====================
const TERMS_DEFAULTS = {
  overline: 'Legal',
  title: 'Terms & Conditions',
  last_updated: 'February 2026',
  intro:
    'Welcome to Rishidev Foundation (rishidevfoundation.in). By browsing, accessing, or making donations/payments on this website, you agree to comply with and be bound by the following terms and conditions of use.',
  sections: [
    {
      heading: 'General',
      body:
        'Rishidev Foundation is a non-profit organization registered in India, dedicated to Vedic studies, spiritual guidance, courses, cultural development, nature and environment, protection of heritage, educational, research & training, and youth, women empowerment and social welfare.\n\nThe contents of this website are for general information and educational/spiritual purposes only.',
    },
    {
      heading: 'Payments & Donations',
      body:
        'All donations, course enrollment fees, and contributions made through our website/payment gateways are voluntary.\n\nUsers must provide accurate, current, and complete details when making transactions on the platform.',
    },
    {
      heading: 'Intellectual Property',
      body:
        'All content on this website, including text, graphics, logos, course materials, and audio/video recordings, is the intellectual property of Rishidev Foundation and protected by copyright laws.',
    },
    {
      heading: 'Limitation of Liability',
      body:
        'Rishidev Foundation shall not be liable for any indirect, incidental, or consequential damages resulting from the use or inability to use our platform or payment gateway.',
    },
    {
      heading: 'Contact Information',
      body:
        '**Email:** info@rishidevfoundation.in\n**Phone:** +91 9895959558\n**Admin Office:** Vins Villa, Kadungallur Road, Thottakkattukara, Aluva, Ernakulam, Kerala, 683108, India.\n**Registered Office:** 35/1623, 2nd Floor, Jubilee Bazar, Ondane Road, Kannur -1, Kerala, India, 670001.',
    },
  ],
};

export const Terms = () => (
  <LegalPage pageId="terms" defaults={TERMS_DEFAULTS} testId="terms-page" />
);

// ==================== 2. Privacy Policy ====================
const PRIVACY_DEFAULTS = {
  overline: 'Legal',
  title: 'Privacy Policy',
  last_updated: 'February 2026',
  intro:
    'Rishidev Foundation ("we", "our", or "us") respects your privacy and is committed to protecting the personal information you share with us.',
  sections: [
    {
      heading: 'Information Collected',
      body:
        'We collect personal information such as your name, email address, phone number, mailing address, and payment details when you register for courses, attend events, or make donations.',
    },
    {
      heading: 'How We Use Information',
      body:
        'To process transactions, issue tax receipts/acknowledgments for donations, and register you for courses or events.\n\nTo send updates, newsletters, or respond to inquiries sent to us.',
    },
    {
      heading: 'Data Security & Payment Gateway',
      body:
        'We do not store sensitive payment credential details (e.g., credit/debit card numbers or Netbanking credentials) on our servers. All transactions are securely processed through Razorpay.\n\nWe implement standard technical and organizational security measures to protect your personal data.',
    },
    {
      heading: 'Sharing of Information',
      body:
        'We do not sell, trade, or rent personal information to third parties. Information is shared only with trusted payment partners (like Razorpay) solely for completing transactions.',
    },
    {
      heading: 'Contact Us',
      body:
        'For any queries regarding this Privacy Policy, please contact us at info@rishidevfoundation.in.',
    },
  ],
};

export const Privacy = () => (
  <LegalPage pageId="privacy" defaults={PRIVACY_DEFAULTS} testId="privacy-page" />
);

// ==================== 3. Refund Policy ====================
const REFUND_DEFAULTS = {
  overline: 'Legal',
  title: 'Refund Policy',
  last_updated: 'February 2026',
  intro: 'Thank you for supporting Rishidev Foundation.',
  sections: [
    {
      heading: 'Donations',
      body:
        'Donations made to Rishidev Foundation are voluntary contributions toward our spiritual and charitable activities. As a general rule, donations are **non-refundable**.\n\nIn the event of a technical error (e.g., duplicate deduction or incorrect amount entered due to payment gateway error), please notify us within **7 days of the transaction** with proof of payment. Valid requests will be processed for refund within **7–10 working days**.',
    },
    {
      heading: 'Courses & Event Fees',
      body:
        'Cancellation and refund requests for paid courses or events must be submitted via email at least **48 hours prior** to the event/course commencement.\n\nApproved refunds will be processed back to the original payment source via Razorpay within **7–10 business days**.',
    },
    {
      heading: 'Contact for Refund Requests',
      body:
        'Email us at info@rishidevfoundation.in with your transaction ID, registered name, and reason for the refund request.',
    },
  ],
};

export const Refund = () => (
  <LegalPage pageId="refund" defaults={REFUND_DEFAULTS} testId="refund-page" />
);

// ==================== 4. Cancellation Policy ====================
const CANCELLATION_DEFAULTS = {
  overline: 'Legal',
  title: 'Cancellation Policy',
  last_updated: 'February 2026',
  intro: '',
  sections: [
    {
      heading: 'Donation Cancellations',
      body:
        'Once an online donation transaction is successfully processed through the website, it cannot be cancelled.',
    },
    {
      heading: 'Course / Event Registration Cancellations',
      body:
        'Participants may cancel their registration for upcoming courses or events by emailing info@rishidevfoundation.in with their booking/registration ID.\n\nCancellations requested at least **48 hours before the scheduled start time** will be accepted. Requests made after the event or course has begun cannot be cancelled.',
    },
    {
      heading: 'Organization Cancellations',
      body:
        'In the rare event that Rishidev Foundation cancels an event or course, participants will be notified promptly and issued a **100% refund** of any registration fees paid.',
    },
  ],
};

export const Cancellation = () => (
  <LegalPage pageId="cancellation" defaults={CANCELLATION_DEFAULTS} testId="cancellation-page" />
);
